import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, ShoppingCart, Download, Loader2 } from "lucide-react";
import { Header } from "../Header";
import { ShieldPreview } from "../shield/ShieldPreview";
import { PhotoPanel } from "../designer/PhotoPanel";
import { DetailsPanel } from "../designer/DetailsPanel";
import { StatsPanel } from "../designer/StatsPanel";
import { api, errMsg } from "../../lib/api";
import { exportPng } from "../../lib/exportPng";
import { PreviewZoom } from "../designer/PreviewZoom";

const Block = ({ n, title, children, testId }) => (
  <section className="border-2 border-slate-900 bg-white p-4" data-testid={testId}>
    <h2 className="mb-3 flex items-center gap-3 font-display text-lg font-extrabold uppercase"><span className="font-cond flex h-7 w-7 items-center justify-center bg-slate-900 text-lime-300">{n}</span>{title}</h2>
    {children}
  </section>
);

export const ParentEditor = ({ code, squad, player, token, onBack, onLost }) => {
  const nav = useNavigate();
  const svgRef = useRef(null);
  const [design, setDesign] = useState({ ...player.design, guide: false });
  const [busy, setBusy] = useState("");
  const set = (k) => (v) => setDesign((d) => ({ ...d, [k]: v }));

  const save = async (quiet) => {
    setBusy(quiet ? "order" : "save");
    try {
      const body = { player: design.player, stats: squad.allow_parent_stats ? design.stats : null, photo: squad.allow_parent_photo ? design.photo : null };
      await api.put(`/share/${code}/players/${player.id}`, body, { headers: { "X-Parent-Token": token } });
      if (!quiet) toast.success("Saved – your coach can see the update");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 403) onLost();
      return false;
    } finally { setBusy(""); }
  };
  const order = async () => { if (await save(true)) nav(`/order?d=${player.id}`); };
  const onDrag = (target, x, y) => target === "photo" && squad.allow_parent_photo && setDesign((d) => ({ ...d, photo: { ...d.photo, x, y } }));
  let n = 0;

  return (
    <div className="min-h-screen pb-24 lg:pb-10">
      <Header onNew={() => nav("/design")} />
      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-4 sm:px-6 lg:grid-cols-[1fr_420px] lg:py-8">
        <section className="sticky top-[70px] z-30 -mx-4 border-b-2 border-slate-900 bg-slate-100 px-4 py-3 lg:order-2 lg:mx-0 lg:self-start lg:border-2 lg:bg-white lg:p-5 lg:top-24">
          <PreviewZoom prefix="parent-preview" className="checker mx-auto h-[34vh] lg:h-[60vh]"><ShieldPreview ref={svgRef} design={design} onDrag={onDrag} testId="parent-shield-preview" /></PreviewZoom>
        </section>
        <div className="space-y-4 lg:order-1">
          <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="parent-back-button"><ArrowLeft className="h-4 w-4" /> {squad.team_name}</button>
          <h1 className="font-display text-3xl font-black uppercase leading-none sm:text-4xl">Your child's shield</h1>
          {squad.allow_parent_photo
            ? <Block n={++n} title="Photo" testId="parent-photo-section"><PhotoPanel photo={design.photo} onChange={set("photo")} /></Block>
            : <p className="bg-slate-100 p-3 text-sm text-slate-600" data-testid="parent-photo-locked">Your coach is adding photos – email yours to them.</p>}
          <Block n={++n} title="Player details" testId="parent-details-section"><DetailsPanel player={design.player} sport={design.sport} accent={design.colours.accent} onChange={set("player")} hideClub /></Block>
          {squad.allow_parent_stats
            ? <Block n={++n} title="Statistics" testId="parent-stats-section"><StatsPanel stats={design.stats} sport={design.sport} onChange={set("stats")} /></Block>
            : <p className="bg-slate-100 p-3 text-sm text-slate-600" data-testid="parent-stats-locked">Statistics are entered by your coach.</p>}
          <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 gap-2 border-t-2 border-slate-900 bg-white p-3 lg:static lg:flex lg:border-0 lg:bg-transparent lg:p-0">
            <button type="button" onClick={order} disabled={!!busy} className="btn-blaze flex items-center justify-center gap-2 px-4 py-2.5 text-sm" data-testid="parent-order-button">
              {busy === "order" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Order
            </button>
            <button type="button" onClick={() => save(false)} disabled={!!busy} className="flex items-center justify-center gap-2 border-2 border-slate-900 bg-white px-4 py-2 text-sm font-bold" data-testid="parent-save-button">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            <button type="button" onClick={() => exportPng(svgRef.current, "shield-preview.png")} className="flex items-center justify-center gap-2 bg-slate-900 px-4 py-2.5 text-sm font-bold text-white" data-testid="parent-download-button">
              <Download className="h-4 w-4" /> PNG
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};
